/**
 * Typed admin API client generated from the shared ROUTES table.
 *
 * - fetch with credentials (HttpOnly cookie session, BFF pattern)
 * - `x-csrf-token` on every mutating request (double-submit token from the session response)
 * - ApiError envelope with request id; 401 raises a session-lost event the SessionProvider handles
 * - audit event id returned by admin mutations (`x-audit-event-id`) surfaces for toasts
 */
import { api } from '@agency/shared';
import type { z } from 'zod';

type Routes = api.Routes;
export type RouteId = api.RouteId;
export type RouteInput<Id extends RouteId> = z.input<Routes[Id]['requestSchema']>;
export type RouteOutput<Id extends RouteId> = api.RouteResponse<Id>;
/** Path params of a route; the shared RouteParams helper misses optional `paramsSchema`, so derive it here. */
type ParamsOf<R> = R extends { paramsSchema?: infer P } ? (NonNullable<P> extends z.ZodType ? ([NonNullable<P>] extends [z.ZodNever] ? Record<string, never> : z.infer<NonNullable<P>>) : Record<string, never>) : Record<string, never>;
export type RouteParams<Id extends RouteId> = ParamsOf<Routes[Id]>;

export const API_BASE: string = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:4400';

export class ApiError extends Error {
  readonly code: api.ApiErrorCode;
  readonly status: number;
  readonly requestId: string | null;
  readonly details: Record<string, unknown> | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(input: { code: api.ApiErrorCode; message: string; status: number; requestId: string | null; details?: Record<string, unknown>; retryAfterSeconds?: number }) {
    super(input.message);
    this.name = 'ApiError';
    this.code = input.code;
    this.status = input.status;
    this.requestId = input.requestId;
    this.details = input.details;
    this.retryAfterSeconds = input.retryAfterSeconds;
  }

  static from(error: unknown): ApiError {
    if (error instanceof ApiError) return error;
    if (error instanceof Error) return new ApiError({ code: 'internal', message: error.message || 'request failed', status: 0, requestId: null });
    return new ApiError({ code: 'internal', message: 'request failed', status: 0, requestId: null });
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
}

export interface ApiResult<Id extends RouteId> {
  data: RouteOutput<Id>;
  requestId: string | null;
  /** Newest audit event id written by this request (admin mutations). */
  auditEventId: string | null;
  status: number;
}

export interface CallArgs<Id extends RouteId> {
  params?: RouteParams<Id>;
  /** Query (GET/DELETE) or JSON body (POST/PUT/PATCH). */
  input?: RouteInput<Id>;
  signal?: AbortSignal;
}

let csrfToken: string | null = null;
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

type Listener = (error: ApiError) => void;
const unauthorizedListeners = new Set<Listener>();
/** Subscribe to 401 responses (session lost). Returns an unsubscribe. */
export function onUnauthorized(listener: Listener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function toQuery(input: Record<string, unknown> | undefined): string {
  if (!input) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) search.append(key, String(v));
    } else {
      search.set(key, String(value));
    }
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/** Absolute URL for a route (used for downloads and the login redirect). */
export function routeUrl<Id extends RouteId>(id: Id, args: { params?: RouteParams<Id>; query?: Record<string, unknown> } = {}): string {
  const route = api.ROUTES[id];
  const path = api.buildPath(route.path, (args.params ?? {}) as Record<string, string>);
  return `${API_BASE}${path}${toQuery(args.query)}`;
}

async function parseError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get('x-request-id');
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }
  const parsed = api.ApiErrorSchema.safeParse(body);
  if (parsed.success) {
    return new ApiError({
      code: parsed.data.code,
      message: parsed.data.message,
      status: response.status,
      requestId: parsed.data.requestId || requestId,
      ...(parsed.data.details ? { details: parsed.data.details } : {}),
      ...(parsed.data.retryAfterSeconds !== undefined ? { retryAfterSeconds: parsed.data.retryAfterSeconds } : {}),
    });
  }
  const code: api.ApiErrorCode = response.status === 401 ? 'unauthorized' : response.status === 403 ? 'forbidden' : response.status === 404 ? 'not_found' : 'internal';
  return new ApiError({ code, message: `${response.status} ${response.statusText || 'error'}`, status: response.status, requestId });
}

/** Perform one typed request. Throws ApiError. */
export async function call<Id extends RouteId>(id: Id, args: CallArgs<Id> = {}): Promise<ApiResult<Id>> {
  const route = api.ROUTES[id];
  const hasBody = route.method !== 'GET' && route.method !== 'DELETE';
  const path = api.buildPath(route.path, (args.params ?? {}) as Record<string, string>);
  const url = `${API_BASE}${path}${hasBody ? '' : toQuery(args.input as Record<string, unknown> | undefined)}`;
  const headers: Record<string, string> = { accept: 'application/json' };
  if (hasBody) headers['content-type'] = 'application/json';
  if (route.method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(url, {
      method: route.method,
      credentials: 'include',
      headers,
      body: hasBody ? JSON.stringify(args.input ?? {}) : undefined,
      ...(args.signal ? { signal: args.signal } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError({ code: 'internal', message: 'The API is unreachable.', status: 0, requestId: null });
  }

  if (!response.ok) {
    const apiError = await parseError(response);
    // A suspended user gets 403 user_suspended on every route; the session is gone for the SPA too.
    if (response.status === 401 || apiError.code === 'user_suspended') for (const l of unauthorizedListeners) l(apiError);
    throw apiError;
  }

  const requestId = response.headers.get('x-request-id');
  const auditEventId = response.headers.get(api.AUDIT_EVENT_ID_HEADER);
  const text = await response.text();
  const data = (text ? JSON.parse(text) : null) as RouteOutput<Id>;
  if (import.meta.env.DEV) {
    const check = route.responseSchema.safeParse(data);
    if (!check.success) console.warn(`[api] response for ${id} does not match the shared schema`, check.error.issues.slice(0, 5));
  }
  return { data, requestId, auditEventId, status: response.status };
}

/** Public health probe (no session). */
export async function health(signal?: AbortSignal): Promise<api.HealthResponse> {
  const res = await call('health.get', signal ? { signal } : {});
  return res.data;
}

/** Development-only helper: mint an admin session for a seeded staff member (server AGENCY_DEV_LOGIN=1). */
export async function devLogin(email?: string): Promise<void> {
  const response = await fetch(`${API_BASE}/admin/auth/dev-login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    body: JSON.stringify(email ? { email } : {}),
  }).catch(() => {
    throw new ApiError({ code: 'internal', message: 'The API is unreachable.', status: 0, requestId: null });
  });
  if (!response.ok) throw await parseError(response);
}

/** Short form of an id for toasts and metadata lines: first 8 characters. */
export function shortId(id: string | null | undefined): string {
  if (!id) return '--';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}
